import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api.js';
import { useToast } from './ToastContext.jsx';

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const read = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return Array.isArray(v) ? v : [];
  } catch { return []; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage blocked */ }
};

export const DEFAULT_SETTINGS = {
  announcement: '',
  contact: {},
  shipping: { freeOver: 5000, fee: 250 },
  categories: { clothing: [], handbags: [] },
  banners: [],
};

// Stock available for a product, optionally for one size
export const stockFor = (product, size) => {
  if (Array.isArray(product.sizes) && product.sizes.length) {
    if (!size) return product.stock || 0;
    const s = product.sizes.find((x) => x.size === size);
    return s ? s.stock : 0;
  }
  return typeof product.stock === 'number' ? product.stock : 99;
};

export function StoreProvider({ children }) {
  const notify = useToast();
  // loading | ready | down. "down" shows the Coming Soon page.
  const [status, setStatus] = useState('loading');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => read('elegance_cart'));
  const [wishlist, setWishlist] = useState(() => read('elegance_wishlist'));
  const [drawer, setDrawer] = useState(null); // 'cart' | 'wishlist' | null

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      await api.health();
      const [s, p] = await Promise.all([api.settings(), api.products()]);
      setSettings({ ...DEFAULT_SETTINGS, ...s });
      setProducts(Array.isArray(p) ? p : []);
      setStatus('ready');
    } catch {
      setStatus('down');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => write('elegance_cart', cart), [cart]);
  useEffect(() => write('elegance_wishlist', wishlist), [wishlist]);

  // Drop cart lines whose product was removed or is sold out, once products are known
  useEffect(() => {
    if (status !== 'ready') return;
    setCart((prev) => {
      const next = prev.filter((l) => products.some((p) => p.id === l.id));
      return next.length === prev.length ? prev : next;
    });
  }, [status, products]);

  const addToCart = useCallback((product, size, quantity = 1) => {
    const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
    if (hasSizes && !size) { notify('Please select a size', 'error'); return false; }
    const available = stockFor(product, hasSizes ? size : null);
    const key = `${product.id}:${size || ''}`;
    const inCart = (cart.find((l) => l.key === key) || {}).quantity || 0;
    if (available <= 0) { notify('This item is sold out', 'error'); return false; }
    if (inCart + quantity > available) {
      notify(`Only ${available} available${size ? ` in size ${size}` : ''}`, 'error');
      return false;
    }
    setCart((prev) => {
      const found = prev.find((l) => l.key === key);
      if (found) return prev.map((l) => (l.key === key ? { ...l, quantity: l.quantity + quantity } : l));
      return [...prev, { key, id: product.id, name: product.name, price: product.price, image: product.image, size: size || '', quantity }];
    });
    setDrawer('cart');
    return true;
  }, [cart, notify]);

  const setQuantity = useCallback((key, quantity) => {
    setCart((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        const product = products.find((p) => p.id === l.id);
        const max = product ? stockFor(product, l.size || null) : 20;
        return { ...l, quantity: Math.max(1, Math.min(quantity, max, 20)) };
      })
    );
  }, [products]);

  const removeLine = useCallback((key) => setCart((prev) => prev.filter((l) => l.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);
  const toggleWishlist = useCallback((id) =>
    setWishlist((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])), []);

  // Prices in the cart follow the live catalogue
  const lines = useMemo(
    () => cart.map((l) => {
      const p = products.find((x) => x.id === l.id);
      return p ? { ...l, name: p.name, price: p.price, image: p.image } : l;
    }),
    [cart, products]
  );
  const cartCount = lines.reduce((s, l) => s + l.quantity, 0);
  const subtotal = lines.reduce((s, l) => s + l.price * l.quantity, 0);
  const shipping = lines.length && subtotal < (settings.shipping?.freeOver ?? 0) ? settings.shipping?.fee || 0 : 0;

  const value = {
    status, reload: load, settings, products,
    cart: lines, cartCount, subtotal, shipping, total: subtotal + shipping,
    addToCart, setQuantity, removeLine, clearCart,
    wishlist, toggleWishlist,
    drawer, setDrawer,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
